/**
 * Source: docs/rules/03-factions.md
 *
 * Shared between Leviathan and Interceptor -- pilots serve the same powers as
 * captains, so this lives in the setting package rather than in either
 * ruleset.
 *
 * Identity only. Factions carry no mechanical effects of their own; the sole
 * rule that touches one is Leviathan's Princep Quality, which requires the
 * Callistonian Empire.
 *
 * `flag` is an app-relative URL rather than a bundled import, so the paths
 * must stay in step with apps/web/public/art/flags/.
 */

export const FACTION_IDS = [
  "terran-commonwealth",
  "callistonian-empire",
  "babylonian-union",
  "allied-worlds",
  "free-trade-directorate",
] as const;

export type FactionId = (typeof FACTION_IDS)[number];

export interface FactionDef {
  readonly id: FactionId;
  readonly name: string;
  /** Short code used on printed material and card assignment badges. */
  readonly code: string;
  /** App-relative path to the faction flag. Must be same-origin for card export. */
  readonly flag: string;
  /**
   * Rank title by rating, indexed 0-6. The rating itself is identical across
   * factions; only the title differs. Index 6 is reachable only through
   * Leviathan's Distinguished Career Quality.
   */
  readonly rankTitles: readonly string[];
}

export const FACTIONS: Record<FactionId, FactionDef> = {
  "terran-commonwealth": {
    id: "terran-commonwealth",
    name: "Terran Commonwealth",
    code: "TC",
    flag: "/art/flags/terran-commonwealth.jpg",
    rankTitles: [
      "Lieutenant",
      "Captain",
      "Major",
      "Lieutenant Colonel",
      "Colonel",
      "Brigadier General",
      "Major General",
    ],
  },
  "callistonian-empire": {
    id: "callistonian-empire",
    name: "Callistonian Empire",
    code: "CE",
    flag: "/art/flags/callistonian-empire.png",
    rankTitles: [
      "Ensign",
      "Lieutenant",
      "Lieutenant Commander",
      "Commander",
      "Captain",
      "Rear Admiral",
      "Vice Admiral",
    ],
  },
  "babylonian-union": {
    id: "babylonian-union",
    name: "Babylonian Union",
    code: "BU",
    flag: "/art/flags/babylonian-union.png",
    rankTitles: [
      "Lieutenant",
      "Captain",
      "Major",
      "Lieutenant Colonel",
      "Colonel",
      "Brigadier General",
      "Major General",
    ],
  },
  "allied-worlds": {
    id: "allied-worlds",
    name: "Allied Worlds",
    code: "AW",
    flag: "/art/flags/allied-worlds.png",
    rankTitles: [
      "Sub-Lieutenant",
      "Lieutenant",
      "Lieutenant Commander",
      "Commander",
      "Captain",
      "Commodore",
      "Rear Admiral",
    ],
  },
  "free-trade-directorate": {
    id: "free-trade-directorate",
    name: "Free Trade Directorate",
    code: "FC",
    flag: "/art/flags/free-trade-directorate.png",
    rankTitles: [
      "Intern",
      "Assistant Manager",
      "Manager",
      "Senior Manager",
      "Director",
      "Senior Director",
      "Vice President",
    ],
  },
};

export const FACTION_LIST: readonly FactionDef[] = FACTION_IDS.map((id) => FACTIONS[id]);

export const MAX_RANK_RATING = 6;

/**
 * Rank title for a rating within a faction. Returns null when no faction is
 * chosen -- faction is optional, so callers fall back to showing the bare
 * rating rather than forcing a faction choice.
 */
export function rankTitle(factionId: FactionId | null, rating: number): string | null {
  if (factionId === null) return null;
  return FACTIONS[factionId].rankTitles[rating] ?? null;
}
